import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-primary grid place-items-center">
            <span className="text-primary-foreground font-heading font-extrabold">K</span>
          </div>
          <span className="font-heading font-bold text-lg">KangarooLedger</span>
        </div>

        <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-primary" /> Set a new password
        </h2>
        <p className="text-muted-foreground text-sm mt-1">Choose a new password for your account.</p>

        {!token ? (
          <div data-testid="reset-no-token" className="mt-8 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
            This reset link is missing its token. Please request a new one from the{" "}
            <Link to="/login" className="underline">login page</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input id="new-password" type="password" required data-testid="new-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input id="confirm-password" type="password" required data-testid="confirm-password-input" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && (
              <div data-testid="reset-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full h-11 rounded-lg font-medium active:scale-[0.98] transition-transform">
              {loading ? "Please wait…" : "Update password"}
            </Button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          <Link to="/login" data-testid="reset-back-login" className="text-primary font-medium hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
